import React from 'react';
import { Box, IconButton, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

type HorizontalPagerProps = {
  children: React.ReactNode;
  ariaLabel?: string;
  padded?: boolean;
};

/**
 * Simple horizontal pager that scrolls the content by one viewport width per click.
 * Hides the scrollbar and shows left/right controls when overflow is present.
 */
export const HorizontalPager: React.FC<HorizontalPagerProps> = ({ children, ariaLabel, padded = true }) => {
  const theme = useTheme();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);

  const updateCanScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateCanScroll();
    const onScroll = () => updateCanScroll();
    el.addEventListener('scroll', onScroll);
    const ro = new ResizeObserver(updateCanScroll);
    ro.observe(el);
    window.addEventListener('resize', updateCanScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateCanScroll);
    };
  }, [updateCanScroll]);

  const page = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'stretch' }}>
      <Box
        ref={scrollRef}
        role="region"
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1.25,
          overflowX: 'hidden',
          overflowY: 'hidden',
          px: padded ? '40px' : 0, // create space for nav buttons overlay
          py: 1,
          width: '100%',
          scrollSnapType: 'x mandatory',
        }}
      >
        {children}
      </Box>

      <IconButton
        aria-label="Previous"
        onClick={() => page(-1)}
        disabled={!canLeft}
        size="small"
        sx={{
          position: 'absolute',
          left: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: theme.palette.background.default,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          '&:disabled': { opacity: 0.4 },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <IconButton
        aria-label="Next"
        onClick={() => page(1)}
        disabled={!canRight}
        size="small"
        sx={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: theme.palette.background.default,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          '&:disabled': { opacity: 0.4 },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default HorizontalPager;
